import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import SettingsHelp from 'bundles/author-common/components/SettingsHelp';
import SetupModal from 'bundles/author-common/components/setup/SetupModal';
import { createNewPartner } from 'bundles/author-partner/actions/AuthoringPartnerActions';
import { PartnerTypeValues } from 'bundles/author-partner/constants/PartnerType';
import type { PartnerTypeValuesType } from 'bundles/author-partner/constants/PartnerType';
import Modal from 'bundles/phoenix/components/Modal';
import BounceLoader from 'bundles/teach-course/components/BounceLoader';
import TextInputEditor from 'bundles/teach-course/components/TextInputEditor';

import _t from 'i18n!nls/author-partner';

import PartnerShortNameEditor from './PartnerShortNameEditor';
import PartnerTypeEditor from './PartnerTypeEditor';

type Props = {
  onCancel: () => void;
  onSubmit?: () => void;
};

type State = {
  showCreateModal: boolean;
  showLoadingModal: boolean;
  name: string;
  shortName: string;
  abbrName: string;
  partnerType: PartnerTypeValuesType;
  isShortNameValid: boolean;
};

class SetupPartnerModal extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof SetupPartnerModal.contextTypes>;

  state = {
    showCreateModal: true,
    showLoadingModal: false,
    name: '',
    shortName: '',
    abbrName: '',
    partnerType: PartnerTypeValues.UNIVERSITY,
    isShortNameValid: false,
  };

  handleNameChange = (name: string) => {
    this.setState({ name });
  };

  handleShortNameChange = (shortName: string, isShortNameValid: boolean) => {
    this.setState({ shortName, isShortNameValid });
  };

  handleAbbrNameChange = (abbrName: string) => {
    this.setState({ abbrName });
  };

  handlePartnerTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const partnerType = event.target.value as PartnerTypeValuesType;
    this.setState({ partnerType });
  };

  handleSubmit = () => {
    const { executeAction } = this.context;
    const { onSubmit } = this.props;
    const { name, shortName, abbrName, partnerType } = this.state;

    this.setState({
      showCreateModal: false,
      showLoadingModal: true,
    });

    executeAction(createNewPartner, { name, shortName, abbrName, partnerType });

    if (onSubmit) {
      onSubmit();
    }
  };

  render() {
    const { showCreateModal, showLoadingModal, name, shortName, abbrName, isShortNameValid, partnerType } = this.state;
    const { onCancel } = this.props;

    const panels = [
      {
        title: _t('Institution name'),
        body: (
          <div style={{ width: '100%' }}>
            <TextInputEditor defaultValue={name} onChange={this.handleNameChange} />
          </div>
        ),
        completed: name.length > 0,
      },
      {
        title: _t('Institution short name'),
        body: <PartnerShortNameEditor shortName={shortName} onChange={this.handleShortNameChange} />,
        completed: isShortNameValid,
      },
      {
        title: _t('Institution abbreviated name'),
        body: (
          <div>
            <SettingsHelp>
              {_t(
                `Many official university names are long,
                and there are some places in the interface where we'd prefer
                to use an abbreviated name, so that we do not have to truncate the long name.`
              )}
            </SettingsHelp>
            <TextInputEditor defaultValue={abbrName} onChange={this.handleAbbrNameChange} />
          </div>
        ),
        completed: abbrName.length > 0,
      },
      {
        title: _t('Partner type'),
        body: <PartnerTypeEditor partnerType={partnerType} onChange={this.handlePartnerTypeChange} />,
        completed: true,
      },
    ];

    return (
      <div className="rc-SetupPartnerModal">
        {showCreateModal && (
          <SetupModal
            heading={_t('Create Institution')}
            panels={panels}
            onCancel={onCancel}
            onSubmit={this.handleSubmit}
          />
        )}

        {showLoadingModal && (
          <Modal modalName={_t('Partner Creation Loading')}>
            <h2 className="create-partner-loading-text">
              {_t('Please wait for the partner to be created, you will be redirected momentarily')}
            </h2>

            <BounceLoader />
          </Modal>
        )}
      </div>
    );
  }
}

export default SetupPartnerModal;
